import {
  AbsoluteCenter,
  Badge,
  Card,
  CardBody,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ButtonMinor from '../../../../components/ButtonMinor';
import {useEnterKey} from '../../../../hooks/useEnterKey';
import useLocalStorageState from '../../../../hooks/useLocalStorageState';
import {useRetryOnAccessTokenRefresh} from '../../../../hooks/useRetryOnAccessTokenRefresh';
import {
  folderEditFormSaveRequest,
  folderEditFormSet,
  pinSaveRequest,
  pinsBalanceRequest,
} from '../../../../reducers/pinsReducer';
import {sendDragWithinPage} from '../../../../vuplex';
import PinEditFoldersList from '../../PinEditFoldersList';
import PinFolderCreateForm from '../../PinFolderCreateForm';
import UpgradeButton from '../../UpgradeButton';
import useReverseGeocode from '../../../../hooks/useReverseGeocode';


const ToastContent = ({
  address: _address,
  altitude,
  isAltitudeDefined,
  latitude,
  longitude,
  onClose,
  suggestedLabel: _suggestedLabel,
  zoomLevel,
}) => {

  const geocode = useReverseGeocode(latitude, longitude, 30, !!_suggestedLabel);
  const suggestedLabel = _suggestedLabel || geocode.displayName;
  const address = _address || geocode.displayName;

  const dispatch = useDispatch();

  const [showDialog, setShowDialog] = useState(true);
  const {_id: worldId, rules} = useSelector(s => s.inApp.world) || {};
  const {isPrivate: isPrivateWorld, isSolo: isSoloWorld} = rules || {};

  const [pinLabel, setPinLabel] = useState(suggestedLabel);
  const [recentFolderId, setRecentFolderId] = useLocalStorageState('pinSaveRecentFolder', null);
  const [selectedFolderId, setSelectedFolderId] = useState(recentFolderId);
  const folderEditForm = useSelector(s => s.pins.folderEditForm);
  const showFolderEditForm = !_.isEmpty(folderEditForm);
  const isFolderFormValid = showFolderEditForm
    ? !!folderEditForm.label
    : !!selectedFolderId;


  useEffect(() => {
    setPinLabel(suggestedLabel);
  }, [suggestedLabel]);

  const canSave = useRef(true);
  const toast = useToast();

  const savePlace = async (folderId) => {
    if (!canSave.current) {
      console.warn('already saved');
      return;
    }

    canSave.current = false;

    setRecentFolderId(folderId);

    setShowDialog(false);

    const {error, payload} = await dispatch(pinSaveRequest({
      address: address || 'unknown address',
      altitude: isAltitudeDefined ? altitude : null,
      label: pinLabel,
      latitude,
      longitude,
      folderId,
      panoId: null,
      panoLocation: {latitude, longitude},
      suggestedLabel,
      zoomLevel,
    }));

    if (error) {
      toast({
        description: 'Unable to save place. Please try again.',
        duration: 1000,
        onCloseComplete: onClose,
        status: 'error',
      });
      canSave.current = true;
    } else {
      const {isInPrivateFolder, isInSharedFolder} = payload;
      const showInvisibleWarning = isPrivateWorld
        ? !isSoloWorld && isInPrivateFolder && !isInSharedFolder
        : isInPrivateFolder;

      toast({
        description: `Place saved${showInvisibleWarning ? ' (not visible to others in this world)' : ''}`,
        duration: 1000,
        onCloseComplete: onClose,
        status: 'success',
      });

      // if (showBalance) {
      //   toast({
      //     status: 'info',
      //     description: pinsBalance === 1
      //       ? `Upgrade to save more places`
      //       : `You may save ${pinsBalance - 1} more ${pinsBalance === 2 ? 'place' : 'places'}`,
      //   });
      // }
    }
  };

  const handleSaveClick = async () => {
    sendDragWithinPage(); // ensure map pan and pinch to zoom work after editing text

    if (showFolderEditForm) {
      const {error, payload} = await dispatch(folderEditFormSaveRequest());
      if (error) {
        toast({
          status: 'error',
          description: 'Unable to create Place List. Please try again or pick an existing list.',
        });
      } else {
        await savePlace(payload._id);
      }
    } else {
      await savePlace(selectedFolderId);
    }
  };

  useRetryOnAccessTokenRefresh(() => dispatch(pinsBalanceRequest({})));
  const {pinsBalance} = useSelector(s => s.pins.pinsBalanceRequest.response) || {};
  const hasBalance = pinsBalance > 0;
  const showBalance = hasBalance && pinsBalance <= 10;

  const disabledReason = !hasBalance
    ? 'Upgrade to save more places'
    : !pinLabel
      ? 'Please enter a description'
      : !isFolderFormValid
        ? showFolderEditForm
          ? 'Please name your new list'
          : 'Please select a list'
        : null;

  useEnterKey(async () => {
    if (!disabledReason && !showFolderEditForm)
      await handleSaveClick();
  });

  // console.log('pano', showDialog, showSaveForm)


  return (
    <Flex
      alignItems="start"
      gap={3}
      w="full"
    >
      {showDialog && (
        <Stack>
          <Card>
            <CardBody>
              <Flex
                p={2}
                gap={2}
              >
                <FormControl isRequired>
                  <FormLabel>Description</FormLabel>
                  <Input

                    onChange={(evt) => setPinLabel(evt.target.value)}
                    value={pinLabel}
                  />
                  <FormHelperText>Add a description</FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>List</FormLabel>
                  {showFolderEditForm
                    ? <PinFolderCreateForm/>
                    : (
                      <PinEditFoldersList
                        onFolderClick={(f) => {
                          if (f) setSelectedFolderId(f._id);
                          else {
                            dispatch(folderEditFormSet({
                              label: '',
                              isSolo: false,
                              sharedInWorld: '',
                            }));
                          }
                        }}
                        popoverHeight="110px"
                        selectedFolderId={selectedFolderId}
                      />)}
                  <FormHelperText>Choose a or create a Place List</FormHelperText>
                </FormControl>
              </Flex>
              <Center gap={2}>
                <ButtonMinor
                  shadow="md"
                  onClick={() => {
                    onClose();
                    sendDragWithinPage(); // ensure map pan and pinch to zoom work after editing text
                  }}
                >
                  Cancel
                </ButtonMinor>

                <ButtonMinor
                  disabledReason={disabledReason}
                  shadow="md"
                  onClick={handleSaveClick}
                >
                  Save to Places

                  {showBalance && (
                    <Badge
                      bg="wooorldBlue._"
                      borderRadius="3xl"
                      bottom="-0.5em"
                      color="white"
                      paddingX={2}
                      position="absolute"
                      right={0}
                    >
                      {pinsBalance}
                    </Badge>
                  )}
                </ButtonMinor>

                {!hasBalance && (
                  <AbsoluteCenter w="10em">
                    <UpgradeButton
                      descriptor="PinEditForm"
                      top="1.8em"
                      right={0}
                      packageName="explorer+"
                      position="absolute"
                      transform="scale(0.8)"
                      zIndex="sticky"
                    />
                  </AbsoluteCenter>
                )}
              </Center>
            </CardBody>

          </Card>
        </Stack>
      )}
    </Flex>


  );
};

const LocationSaveToast = ({
  address,
  altitude,
  isAltitudeDefined,
  isDisabled,
  latitude,
  longitude,
  onClose,
  suggestedLabel,
  zoomLevel,
}) => {
  const toast = useToast();
  const toastIdRef = useRef();

  useEffect(() => {
    const closeCurrentToast = () => toast.close(toastIdRef.current);

    if (!isDisabled) {
      toast.closeAll();
      toastIdRef.current = toast({
        render: () => (
          <ToastContent
            address={address}
            altitude={altitude}
            isAltitudeDefined={isAltitudeDefined}
            latitude={latitude}
            longitude={longitude}
            suggestedLabel={suggestedLabel}
            zoomLevel={zoomLevel}
            onClose={() => {
              closeCurrentToast();
              onClose();
            }}
          />),
        duration: null,
        icon: <></>,
        isClosable: false,
        position: 'bottom',
      });
    } else {
      closeCurrentToast();
    }

    return closeCurrentToast;
  }, [
    altitude,
    isDisabled,
    latitude,
    longitude,
    zoomLevel,
  ]);

  return null;
};

export default LocationSaveToast;
